import { h } from "preact";
import MonogramTypeSelector from "./monogram-type-selector";
import {
  MONOGRAM_TYPES,
  INITIALS,
  LINES,
  INITIAL_MAX_LENGTH,
  LINE_MAX_LENGTH,
  applyLineRestrictions,
  monogramFlag,
  monogramTextForLine,
} from "./monogramTypeConfig";

/**
 * Content of the "Personalization" accordion (step 1): the type-selector grid
 * followed by the input fields for the currently selected type (a row of
 * single-character boxes for INITIALS, full-width text inputs for LINES).
 *
 * State lives in the parent (<MonogramContent />); this component is controlled
 * via `type`/`values`/`style` and reports changes through callbacks.
 */
export default function MonogramPersonalization({
  types,
  type,
  values,
  style,
  onTypeChange,
  onValueChange,
  strings,
}) {
  const config = MONOGRAM_TYPES[type];
  const typeStrings = strings.types || {};
  const fieldStrings = strings.fields || {};

  const labelFor = (t) => typeStrings[MONOGRAM_TYPES[t]?.labelKey] || t;

  const handleInitial = (key) => (event) => {
    const value = event.target.value.replace(/[^a-zA-Z]/g, "").toUpperCase();
    onValueChange(key, value.slice(0, INITIAL_MAX_LENGTH));
  };

  const handleLine = (field, entry) => (event) => {
    onValueChange(field.key, applyLineRestrictions(event.target.value, entry));
  };

  return (
    <div className="monogram-personalization">
      <div className="monogram-personalization__section">
        <p className="monogram-personalization__instruction body-2">
          {strings.select_type}
        </p>
        <MonogramTypeSelector
          types={types}
          selected={type}
          onSelect={onTypeChange}
          labelFor={labelFor}
        />
      </div>

      {config && config.mode === INITIALS && (
        <div className="monogram-personalization__section">
          <p className="monogram-personalization__instruction body-2">
            {strings.initials_instructions}
          </p>
          <div className="monogram-personalization__initials">
            {config.fields.map((field) => {
              const label = fieldStrings[field.labelKey] || "";
              return (
                <label
                  key={field.key}
                  className="monogram-personalization__initial"
                >
                  <input
                    className="monogram-personalization__initial-input"
                    type="text"
                    inputMode="text"
                    autoComplete="off"
                    maxLength={INITIAL_MAX_LENGTH}
                    aria-label={label}
                    value={values[field.key] || ""}
                    onInput={handleInitial(field.key)}
                  />
                  <span className="monogram-personalization__initial-label caption-1">
                    {label}
                  </span>
                </label>
              );
            })}
          </div>
        </div>
      )}

      {config &&
        config.mode === LINES &&
        config.fields.map((field, index) => {
          const label = field.labelKey ? fieldStrings[field.labelKey] : "";
          const entry = monogramTextForLine(style, index);
          const uppercase = monogramFlag(entry?.uppercase);
          return (
            <div className="monogram-personalization__section" key={field.key}>
              {label && (
                <p className="monogram-personalization__line-label utility-2">
                  {label}
                </p>
              )}
              <p className="monogram-personalization__instruction body-2">
                {strings.message_instructions}
              </p>
              <input
                className={`monogram-personalization__line-input ${
                  uppercase
                    ? "monogram-personalization__line-input--uppercase"
                    : ""
                }`}
                type="text"
                autoComplete="off"
                maxLength={LINE_MAX_LENGTH}
                aria-label={label || strings.message_instructions}
                value={values[field.key] || ""}
                onInput={handleLine(field, entry)}
              />
            </div>
          );
        })}
    </div>
  );
}
